import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import styled from 'styled-components';
import Scanner from '../components/Scanner';
import LoadingSpinner from '../components/LoadingSpinner';
import Button from '../components/Button';
import { ProjectSelector } from '../components/ProjectSelector';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { Clock, CheckCircle2, AlertTriangle, QrCode, Box, BarChart, User, Calendar, MessageSquare, Search, Filter, ArrowLeft, CheckCircle, Truck, Package, X } from 'lucide-react';
import { apiService } from '../services/api';
import { syncManager } from '../services/syncManager';
import { useProject } from '../contexts/ProjectContext';

// Types
type ScanningMode = 'carton' | 'item' | 'content';

interface CartonStats {
  total: number;
  received: number;
  pending: number;
  notFound: number;
  progress: number;
  totalPanels?: number;
  receivedPanels?: number;
  totalHardware?: number;
  receivedHardware?: number;
}

interface BaseItem {
  id: string;
  type: string;
  name: string;
  code: string;
  isReceived: boolean;
  is_received?: boolean; // For API compatibility
}

interface ProjectCartonPanel extends BaseItem {
  type: 'panel';
  panel_id?: string; // For API compatibility
}

interface ProjectHardware extends BaseItem {
  type: 'hardware';
  hardware_id?: string; // For API compatibility
  quantity: number;
  receivedQuantity: number;
}

type CartonContentItem = ProjectCartonPanel | ProjectHardware;

interface ProjectCarton {
  id: string;
  code: string;
  panels: ProjectCartonPanel[];
  hardware: ProjectHardware[];
  isReceived: boolean;
  is_received?: boolean; // For API compatibility
}

interface ScannedItem {
  id: string;
  type: 'carton' | 'panel' | 'hardware';
  code: string;
  received: boolean;
  timestamp: Date;
  isProcessed: boolean;
  data: CartonContentItem | ProjectCarton | null;
}

interface ConfirmationData {
  title: string;
  message: string;
  onConfirm: () => void;
  type?: 'unassigned' | 'content_not_in_carton' | 'different_carton' | 'confirm_receipt' | string;
  content?: string;
  isPanel?: boolean;
  isHardware?: boolean;
}

interface CartonContentState {
  loading: boolean;
  scanningMode: ScanningMode;
  currentCarton: ProjectCarton | null;
  cartonContents: {
    panels: ProjectCartonPanel[];
    hardware: ProjectHardware[];
  };
  scannedItems: ScannedItem[];
  pendingCarton: ProjectCarton | null;
  showSwitchConfirm: boolean;
  showConfirmation: boolean;
  confirmationData: ConfirmationData | null;
  loadedProjectId: string | null;
  showProjectSelector: boolean;
  detailedProject: Project | null;
  isMobileMenuOpen: boolean;
  showScanner: boolean;
}

interface Project {
  project: {
    id: string;
    code: string;
    display_name: string;
    customer_name: string;
    so_number?: string;
  };
  statistics: {
    cartons: {
      total_cartons: number;
      received_cartons: number;
      pending_cartons: number;
    };
    panels: {
      total_panels: number;
      received_panels: number;
      pending_panels: number;
    };
    hardware: {
      total_hardware: number;
      received_hardware: number;
      pending_hardware: number;
    };
  };
  cartons: ProjectCarton[];
  panels: ProjectCartonPanel[];
  hardware: ProjectHardware[];
}

// Scanner ref type
type ScannerRefType = {
  updateCartonStats: (stats: CartonStats) => void;
};

// Styled components
const Container = styled.div`
  padding: 1rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    padding: 0.5rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1rem;
  background: ${props => props.theme.colors.background};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const ProjectSummary = styled.div`
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-1px);
  }
`;

const ProjectHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const ProjectTitle = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const ProjectSubtitle = styled.div`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const SwitchButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;

  &:hover {
    text-decoration: underline;
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
`;

const CartonStats = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const StatItem = styled.div`
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 6px;
  padding: 0.75rem;
  flex: 1;
  min-width: 200px;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: 0.25rem;
`;

const StatValue = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const StatSubValue = styled.div`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.textSecondary};
  margin-top: 0.25rem;
`;

const CartonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 0.5rem;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.backgroundSecondary};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.border};
    border-radius: 3px;
    
    &:hover {
      background: ${props => props.theme.colors.textSecondary};
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const CartonCard = styled.div`
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
  }
`;

const CartonHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const CartonCode = styled.span`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const CartonContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ContentItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background: ${props => props.theme.colors.gray50};
  border-radius: 4px;
`;

const ScanButton = styled.button`
  position: fixed;
  bottom: 80px;
  right: 1rem;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  z-index: 100;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    bottom: 70px;
  }

  &:hover {
    background: ${props => props.theme.colors.primaryDark};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const EmptyStateIcon = styled.div`
  margin-bottom: 1rem;
`;

const EmptyStateText = styled.span`
  font-size: 1rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const ProjectStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  padding: 1rem;
`;

const StatHeader = styled.div`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: 0.5rem;
`;

const StatBreakdown = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: ${props => props.theme.colors.textSecondary};
`;

// Add new styled components for mobile menu
const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${props => props.theme.colors.text};
  padding: 0.5rem;
  cursor: pointer;
  margin-left: 1rem;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const MobileMenu = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 80%;
  max-width: 300px;
  height: 100vh;
  background: ${props => props.theme.colors.background};
  z-index: 1001;
  transform: translateX(${props => props.$isOpen ? '0' : '100%'});
  transition: transform 0.3s ease;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
`;

const MobileMenuHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.background};
`;

const MobileMenuContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
`;

const MobileMenuItem = styled.button`
  width: 100%;
  padding: 1rem;
  text-align: left;
  background: none;
  border: none;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.text};
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: ${props => props.theme.colors.gray50};
  }

  &:active {
    background: ${props => props.theme.colors.gray100};
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.text};
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MenuOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease;
`;

// Add these styled components for the carton information section
const CartonInfoSection = styled.div`
  margin: 1.5rem 0;
  padding: 1rem;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const CartonInfoHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const CartonInfoTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ItemsList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  padding-right: 0.5rem;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.background};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: 3px;
  }
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  
  &:last-child {
    border-bottom: none;
  }
`;

const ItemInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ItemStatus = styled.span<{ $received: boolean }>`
  font-size: 0.8rem;
  color: ${({ $received, theme }) => 
    $received ? theme.colors.success : theme.colors.warning};
`;

// ScannedItem type is already defined earlier in the file

const CartonContentReceivingPage = () => {
  // Get selected project from global context
  const { selectedProject } = useProject();
  
  // State management
  const [state, setState] = useState<CartonContentState>({
    loading: false,
    scanningMode: 'carton',
    currentCarton: null,
    cartonContents: { panels: [], hardware: [] },
    scannedItems: [],
    pendingCarton: null,
    showSwitchConfirm: false,
    showConfirmation: false,
    confirmationData: null,
    loadedProjectId: null,
    showProjectSelector: false,
    detailedProject: null,
    isMobileMenuOpen: false,
    showScanner: false,
  });

  // Scanner ref
  const scannerRef = useRef<ScannerRefType>({
    updateCartonStats: (stats: CartonStats) => {
      console.log('Updating carton stats:', stats);
    }
  });

  // Sync status monitoring
  const [syncStatus, setSyncStatus] = useState(syncManager.getSyncStatus());

  const {
    currentCarton,
    cartonContents,
    scannedItems,
    pendingCarton,
    showSwitchConfirm,
    confirmationData,
    loadedProjectId,
    showProjectSelector,
    detailedProject,
    isMobileMenuOpen,
    loading,
    scanningMode,
    showConfirmation,
    showScanner,
  } = state as CartonContentState;

  // Sync status monitoring
  useEffect(() => {
    const unsubscribe = syncManager.onSyncStatusChange(setSyncStatus);
    return unsubscribe;
  }, []);

  // Debug: Log current state values
  console.log('[DEBUG] Current state values:', {
    currentCarton: currentCarton?.code,
    cartonContents: cartonContents ? { panels: cartonContents.panels?.length, hardware: cartonContents.hardware?.length } : null,
    scanningMode,
    showScanner
  });

  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const updateState = useCallback((updates: Partial<CartonContentState>) => {
    setState(prev => ({
      ...prev,
      ...updates
    }));
  }, []);

  const updateCartonStats = useCallback((contents: { panels: ProjectCartonPanel[], hardware: ProjectHardware[] }, items: ScannedItem[]) => {
    const totalPanels = contents.panels.length;
    const receivedPanels = contents.panels.filter(c => c.isReceived).length;
    const totalHardware = contents.hardware.length;
    const receivedHardware = contents.hardware.filter(c => c.isReceived).length;
    const total = totalPanels + totalHardware;
    const received = receivedPanels + receivedHardware;
    const remaining = total - received;
    const progress = total > 0 ? Math.round((received / total) * 100) : 0;
    
    const stats: CartonStats = {
      total,
      received,
      remaining,
      progress,
      totalPanels,
      receivedPanels,
      totalHardware,
      receivedHardware,
      pendingItems: items.filter(item => !item.isProcessed).length
    };
    
    scannerRef.current?.updateCartonStats(stats);
  }, []);

  const setShowConfirmation = (show: boolean) => {
    updateState({ showConfirmation: show });
  };
  
  // Set pending carton (for carton switching)
  const setPendingCarton = (carton: ProjectCarton | null) => {
    updateState({ pendingCarton: carton });
  };
  
  const loadProjectData = useCallback(async () => {
    if (!selectedProject) {
      console.error('[ERROR] No project selected');
      return;
    }
    
    console.log('[DEBUG] Loading project data for project:', selectedProject.id);
    
    try {
      const projectData = await apiService.getProject(selectedProject.id);
      
      console.log('[DEBUG] Project data loaded:', projectData);
      
      updateState({
        detailedProject: projectData,
        loadedProjectId: selectedProject.id,
        loading: false
      });
    } catch (error) {
      console.error('[ERROR] Error loading project:', error);
      updateState({ loading: false });
      
      // Check if we're offline
      const isOffline = !navigator.onLine;
      if (isOffline) {
        console.log('[DEBUG] Offline mode detected, showing offline message');
        toast.info('Working in offline mode. Scanned items will be synced when online.');
      } else {
        toast.error('Failed to load project data');
      }
    }
  }, [selectedProject]);

  const loadCartonContents = useCallback(async (cartonCode: string) => {
    if (!selectedProject) {
      console.error('[ERROR] No project selected');
      return false;
    }
    
    console.log('[DEBUG] Loading carton contents for carton:', cartonCode);
    
    try {
      // Find the carton in the project data
      const carton = detailedProject?.cartons.find(c => c.code === cartonCode);
      if (!carton) {
        console.error('[ERROR] Carton not found:', cartonCode);
        toast.error('Carton not found');
        return false;
      }

      // Debug: Log the carton structure and project data
      console.log('[DEBUG] Carton structure:', carton);
      console.log('[DEBUG] Carton panels:', carton.panels);
      console.log('[DEBUG] Carton hardware:', carton.hardware);

      // Try multiple approaches to get carton contents
      let panels = [];
      let hardware = [];

      // Approach 1: Check if carton has items arrays directly
      if (carton.panels && Array.isArray(carton.panels)) {
        panels = carton.panels;
        console.log('[DEBUG] Using carton.panels array:', panels.length);
      } else if (carton.panels && carton.panels.items && Array.isArray(carton.panels.items)) {
        panels = carton.panels.items;
        console.log('[DEBUG] Using carton.panels.items array:', panels.length);
      } else {
        // Approach 2: Filter from project data
        panels = detailedProject?.panels?.filter((panel: any) => {
          const matches = panel.carton_id === carton.id || 
                         panel.carton_code === carton.code ||
                         panel.cartonId === carton.id ||
                         panel.cartonCode === carton.code;
          if (matches) {
            console.log('[DEBUG] Found matching panel:', panel);
          }
          return matches;
        }) || [];
        console.log('[DEBUG] Filtered panels from project:', panels.length);
      }

      if (carton.hardware && Array.isArray(carton.hardware)) {
        hardware = carton.hardware;
        console.log('[DEBUG] Using carton.hardware array:', hardware.length);
      } else if (carton.hardware && carton.hardware.items && Array.isArray(carton.hardware.items)) {
        hardware = carton.hardware.items;
        console.log('[DEBUG] Using carton.hardware.items array:', hardware.length);
      } else {
        // Approach 2: Filter from project data
        hardware = detailedProject?.hardware?.filter((hw: any) => {
          const matches = hw.carton_id === carton.id || 
                         hw.carton_code === carton.code ||
                         hw.cartonId === carton.id ||
                         hw.cartonCode === carton.code;
          if (matches) {
            console.log('[DEBUG] Found matching hardware:', hw);
          }
          return matches;
        }) || [];
        console.log('[DEBUG] Filtered hardware from project:', hardware.length);
      }

      console.log('[DEBUG] Carton contents - Panels:', panels.length, 'Hardware:', hardware.length);

      // If no items found, show empty state
      if (panels.length === 0 && hardware.length === 0) {
        console.log('[DEBUG] No items found in carton');
      }

      // Update the current carton and its contents
      updateState({ 
        currentCarton: carton,
        cartonContents: { panels, hardware }
      });
      
      console.log('[DEBUG] Carton contents loaded successfully');
      console.log('[DEBUG] Updated state - currentCarton:', carton.code, 'cartonContents:', { panels: panels.length, hardware: hardware.length });
      return true;
    } catch (error) {
      console.error('[ERROR] Error loading carton contents:', error);
      toast.error('Failed to load carton contents');
      return false;
    }
  }, [selectedProject, detailedProject]);

  // Load project data when selectedProject changes
  useEffect(() => {
    if (selectedProject && selectedProject.id !== loadedProjectId) {
      console.log('[DEBUG] Project changed, loading project data:', selectedProject.id);
      updateState({ loading: true });
      loadProjectData();
    }
  }, [selectedProject, loadedProjectId, loadProjectData]);

  useEffect(() => {
    console.log('Component mounted/updated');
    console.log('Current carton:', currentCarton);
    console.log('Carton contents:', cartonContents);
    return () => console.log('Component unmounting');
  }, [currentCarton, cartonContents]);

  // Debug scanner overlay rendering
  useEffect(() => {
    if (showScanner && currentCarton) {
      console.log('[DEBUG] Scanner overlay rendering - currentCarton:', currentCarton, 'cartonContents:', cartonContents);
    }
  }, [showScanner, currentCarton, cartonContents]);
  // Add missing scan handler functions
  const handleCartonScan = async (cartonCode: string) => {
    console.log('[DEBUG] Carton scan received:', cartonCode);
    await handleScan(cartonCode);
  };

  const handlePanelScan = async (panel: any) => {
    console.log('[DEBUG] Panel scan received:', panel);
    if (!panel || !panel.code) {
      console.error('[ERROR] Invalid panel data');
      return;
    }

    if (!currentCarton) {
      console.error('[ERROR] No current carton for panel processing');
      toast.error('No carton selected');
      return;
    }

    try {
      console.log('[DEBUG] Processing panel:', panel.code, 'in carton:', currentCarton.code);
      console.log('[DEBUG] Panel data:', panel);
      console.log('[DEBUG] Current carton data:', currentCarton);
      
      // Process the panel directly
      const response = await apiService.receiveCartonContent({
        cartonId: currentCarton.id,
        contentCode: panel.code,
        projectId: selectedProject?.id || '',
        isPanel: true,
        isHardware: false
      });

      console.log('[DEBUG] API response:', response);

      if (response.success) {
        console.log('[DEBUG] Panel processed successfully:', response.data);
        toast.success(`Panel ${panel.code} received successfully`);
        
        // Reload carton contents to update the UI
        console.log('[DEBUG] Reloading carton contents to update UI');
        await loadCartonContents(currentCarton.code);
        
        // Also reload project data to ensure consistency
        console.log('[DEBUG] Reloading project data to ensure consistency');
        await loadProjectData();
      } else {
        console.error('[ERROR] Failed to process panel:', response.message);
        toast.error(response.message || 'Failed to process panel');
      }
    } catch (error) {
      console.error('[ERROR] Error processing panel:', error);
      toast.error('Failed to process panel');
    }
  };

  const handleHardwareScan = async (hardware: any) => {
    console.log('[DEBUG] Hardware scan received:', hardware);
    if (!hardware || !hardware.code) {
      console.error('[ERROR] Invalid hardware data');
      return;
    }

    if (!currentCarton) {
      console.error('[ERROR] No current carton for hardware processing');
      toast.error('No carton selected');
      return;
    }

    try {
      console.log('[DEBUG] Processing hardware:', hardware.code, 'in carton:', currentCarton.code);
      
      // Process the hardware directly
      const response = await apiService.receiveCartonContent({
        cartonId: currentCarton.id,
        contentCode: hardware.code,
        projectId: selectedProject?.id || '',
        isPanel: false,
        isHardware: true
      });

      if (response.success) {
        console.log('[DEBUG] Hardware processed successfully:', response.data);
        toast.success(`Hardware ${hardware.code} received successfully`);
        
        // Reload carton contents to update the UI
        await loadCartonContents(currentCarton.code);
      } else {
        console.error('[ERROR] Failed to process hardware:', response.message);
        toast.error(response.message || 'Failed to process hardware');
      }
    } catch (error) {
      console.error('[ERROR] Error processing hardware:', error);
      toast.error('Failed to process hardware');
    }
  };

  // Create a ref to track the current carton for reliable access
  const currentCartonRef = useRef(currentCarton);
  currentCartonRef.current = currentCarton;

  const handleSuccessfulScan = (data: any) => {
    // Refresh project data after successful scan
    if (selectedProject) {
      loadProjectData();
    }
    toast.success('Content received successfully');
  };

  const handleScan = useCallback(async (result: string) => {
    console.log('[DEBUG] Scan received:', result);
    console.log('[DEBUG] Current state - Project:', selectedProject?.id, 'Carton:', currentCarton?.code, 'Mode:', scanningMode);

    // Step 1: Validate project selection
    if (!selectedProject) {
      console.log('[ERROR] No project selected');
      toast.error('Please select a project first');
      return;
    }

    try {
      // Step 2: Check if scanned code is a carton
      console.log('[DEBUG] Available cartons:', detailedProject?.cartons?.map((c: any) => c.code));
      console.log('[DEBUG] Looking for carton with code:', result);
      
      const scannedCarton = detailedProject?.cartons?.find((c: any) => c.code === result);
      
      if (scannedCarton) {
        console.log('[DEBUG] Found carton in project:', scannedCarton.code);
        console.log('[DEBUG] Carton object:', scannedCarton);
        
        // If we already have a carton and user scans another one, ask for confirmation
        if (currentCarton && currentCarton.code !== scannedCarton.code) {
          console.log('[DEBUG] Different carton scanned, asking for confirmation');
          
              setPendingCarton(scannedCarton);
              updateState({
                showSwitchConfirm: true,
                confirmationData: {
                  title: 'Switch Carton',
                  message: `Are you sure you want to switch to carton ${scannedCarton.code}?`,
                  onConfirm: async () => {
                    // Load the new carton's contents
                    const loaded = await loadCartonContents(scannedCarton.code);
                    if (loaded) {
                      updateState({
                    currentCarton: scannedCarton,
                        showSwitchConfirm: false,
                        scanningMode: 'content'
                      });
                      toast.success(`Now scanning contents for carton ${scannedCarton.code}`);
                    } else {
                      updateState({ showSwitchConfirm: false });
                      toast.error('Failed to load carton contents');
                    }
                  }
                }
              });
          return;
        }

        // Set the current carton and switch to content scanning mode
        console.log('[DEBUG] Setting current carton and switching to content mode');
        
        // Load the carton contents first
        const loaded = await loadCartonContents(scannedCarton.code);
        if (!loaded) {
          console.error('[ERROR] Failed to load carton contents');
          toast.error('Failed to load carton contents');
          return;
        }
        
        // Update state with both carton and mode
        updateState({ 
          currentCarton: scannedCarton,
          scanningMode: 'content'
        });
        
        console.log('[DEBUG] Carton set successfully:', {
          cartonCode: scannedCarton.code,
          cartonId: scannedCarton.id,
          scanningMode: 'content'
        });
        
        // Verify state was updated
        console.log('[DEBUG] State after update:', {
          currentCarton: currentCarton?.code,
          scanningMode: scanningMode
        });
        
        toast.success(`Now scanning contents for carton ${scannedCarton.code}`);
            return;
      } else {
        console.log('[DEBUG] Carton not found in project cartons, checking if it\'s a panel/hardware...');
      }

      // Step 3: If not a carton, check if we have a current carton selected
      const effectiveCurrentCarton = currentCartonRef.current;
      if (!effectiveCurrentCarton) {
        console.log('[ERROR] No carton selected - cannot scan panels/hardware without a carton');
        console.log('[DEBUG] Current state values:', {
          currentCarton: effectiveCurrentCarton?.code,
          scanningMode: scanningMode,
          selectedProject: selectedProject?.id,
          detailedProject: !!detailedProject
        });
        toast.error('Please scan a carton first before scanning panels or hardware');
        return;
      }

      // Step 4: Check if scanned code is a valid project item (panel or hardware)
      console.log('[DEBUG] Checking if scanned code is a panel or hardware...');
      console.log('[DEBUG] Available panels:', detailedProject?.panels?.slice(0, 3));
      console.log('[DEBUG] Available hardware:', detailedProject?.hardwareLabels?.slice(0, 3));
      console.log('[DEBUG] Looking for code:', result);
      console.log('[DEBUG] Panel codes:', detailedProject?.panels?.map((p: any) => p.code));
      console.log('[DEBUG] Hardware codes:', detailedProject?.hardwareLabels?.map((h: any) => h.code));
      console.log('[DEBUG] First panel object keys:', detailedProject?.panels?.[0] ? Object.keys(detailedProject.panels[0]) : 'No panels');
      console.log('[DEBUG] First panel object:', detailedProject?.panels?.[0]);
      
      // Check panels with multiple possible property names
      const isPanelInProject = detailedProject?.panels?.some((p: any) => 
        p.code === result || 
        p.panel_code === result || 
        p.id === result ||
        p.name === result
      );
      
      // Check hardware with multiple possible property names
      const isHardwareInProject = detailedProject?.hardwareLabels?.some((h: any) => 
        h.code === result || 
        h.hardware_code === result || 
        h.id === result ||
        h.name === result
      );
      
      console.log('[DEBUG] Panel check result:', isPanelInProject);
      console.log('[DEBUG] Hardware check result:', isHardwareInProject);

      // If not a valid project item at all
      if (!isPanelInProject && !isHardwareInProject) {
        console.log('[ERROR] Scanned code not found in project');
        toast.error(`"${result}" is not a valid panel or hardware item in this project`);
          return;
        }

      const itemType = isPanelInProject ? 'panel' : 'hardware';
      console.log(`[DEBUG] Found valid ${itemType} in project:`, result);

      // Step 5: Check if the item belongs to the current carton
      const panelInCarton = (cartonContents.panels || []).find(p => p.code === result);
      const hardwareInCarton = (cartonContents.hardware || []).find(h => h.code === result);

      console.log('[DEBUG] Content in carton - Panel:', panelInCarton, 'Hardware:', hardwareInCarton);

      // If not in current carton, check if it belongs to another carton
      if (!panelInCarton && !hardwareInCarton) {
        console.log(`[DEBUG] ${itemType} not in current carton, checking other cartons...`);
        
        // Check if it's a panel in another carton
        const panelInOtherCarton = detailedProject?.cartons?.find((carton: any) => {
          if (carton.panels && carton.panels.items) {
            return carton.panels.items.some((item: any) => item.code === result);
          }
          return false;
        });

        // Check if it's hardware in another carton
        const hardwareInOtherCarton = detailedProject?.cartons?.find((carton: any) => {
          if (carton.hardware && carton.hardware.items) {
            return carton.hardware.items.some((item: any) => item.code === result);
          }
          return false;
        });

        if (panelInOtherCarton || hardwareInOtherCarton) {
          const targetCarton = panelInOtherCarton || hardwareInOtherCarton;
          
          // Check if the target carton is actually the current carton
          if (targetCarton?.code === effectiveCurrentCarton?.code) {
            console.log(`[DEBUG] ${itemType} belongs to current carton, processing directly`);
            // The item belongs to the current carton, process it directly
            try {
              const response = await apiService.receiveCartonContent({
                cartonId: effectiveCurrentCarton.id,
                contentCode: result,
                projectId: selectedProject.id,
                isPanel: !!panelInOtherCarton,
                isHardware: !!hardwareInOtherCarton
              });

              console.log('[DEBUG] Receive content response:', response);

              if (response.data && response.data.needsConfirmation) {
                console.log('[DEBUG] Needs confirmation');
                updateState({ confirmationData: response.data });
                setShowConfirmation(true);
              } else if (response.data) {
                console.log('[SUCCESS] Content received successfully');
                handleSuccessfulScan(response.data);
              } else {
                console.error('[ERROR] Invalid response format:', response);
                handleScanError(response?.error || { message: 'Unknown error' });
              }
            } catch (error) {
              console.error('[ERROR] Error receiving content:', error);
              toast.error('Error processing content');
            }
            return;
          }
          
          console.log(`[DEBUG] ${itemType} belongs to different carton:`, targetCarton?.code);
          
          setPendingCarton(targetCarton);
              updateState({
                showSwitchConfirm: true,
                confirmationData: {
                  title: 'Switch Carton?',
              message: `${itemType.charAt(0).toUpperCase() + itemType.slice(1)} "${result}" belongs to carton ${targetCarton?.code}. Do you want to switch?`,
                  onConfirm: async () => {
                // Switch to new carton and process the item
                    updateState({
                  currentCarton: targetCarton,
                      scanningMode: 'content',
                      showSwitchConfirm: false
                    });
                    
                const loaded = await loadCartonContents(targetCarton!.code);
                    if (loaded) {
                  // Find and process the item in the new carton
                  const itemToProcess = panelInOtherCarton 
                    ? targetCarton!.panels.items.find((item: any) => item.code === result)
                    : targetCarton!.hardware.items.find((item: any) => item.code === result);
                  
                  if (itemToProcess) {
                    if (panelInOtherCarton) {
                      await handlePanelScan(itemToProcess);
                    } else {
                      await handleHardwareScan(itemToProcess);
                    }
                    toast.success(`Switched to carton ${targetCarton!.code} and processed ${itemType} "${result}"`);
                  }
                }
              }
            }
          });
          return;
        }

        // Item is in project but not assigned to any carton
        console.log(`[DEBUG] ${itemType} found in project but not assigned to any carton`);
        toast.error(`${itemType.charAt(0).toUpperCase() + itemType.slice(1)} "${result}" is not assigned to any carton in this project`);
        return;
      }

      // Step 6: Process the valid content in the current carton
      console.log(`[DEBUG] Processing valid ${itemType} in current carton`);
      
      try {
        const response = await apiService.receiveCartonContent({
          cartonId: effectiveCurrentCarton.id,
          contentCode: result,
          projectId: selectedProject.id,
          isPanel: !!panelInCarton,
          isHardware: !!hardwareInCarton
        });

        console.log('[DEBUG] Receive content response:', response);

        if (response.data && response.data.needsConfirmation) {
          console.log('[DEBUG] Needs confirmation');
          updateState({ confirmationData: response.data });
          setShowConfirmation(true);
        } else if (response.data) {
          console.log('[SUCCESS] Content received successfully');
          handleSuccessfulScan(response.data);
        } else {
          console.error('[ERROR] Invalid response format:', response);
          handleScanError(response?.error || { message: 'Unknown error' });
        }
      } catch (error) {
        console.error('[ERROR] Error receiving content:', error);
        toast.error('Error processing content');
      }

    } catch (error) {
      console.error('[ERROR] Scan error:', error);
      toast.error('Error processing scan');
    }
    }, [selectedProject, detailedProject, cartonContents, updateState, loadCartonContents, setPendingCarton, handlePanelScan, handleHardwareScan, handleSuccessfulScan, setShowConfirmation]);


  const handleScanError = (error: any) => {
    switch (error.code) {
      case 'DIFFERENT_PROJECT':
        toast.error('This content belongs to a different project');
        break;
      case 'ALREADY_RECEIVED':
        toast.error('This content has already been received');
        break;
      case 'UNASSIGNED_CONTENT':
        updateState({
          confirmationData: {
            type: 'unassigned',
            content: error.content,
            message: 'This content is not assigned to any carton. Would you like to assign it to the current carton?'
          }
        });
        setShowConfirmation(true);
        break;
      case 'INVALID_CARTON':
        toast.error('Invalid carton code');
        break;
      case 'CARTON_NOT_FOUND':
        toast.error('Carton not found in this project');
        break;
      case 'CONTENT_NOT_IN_CARTON':
        toast.error('This content does not belong to the current carton');
        break;
      case 'INVALID_CONTENT':
        toast.error('Invalid panel or hardware code');
        break;
      default:
        toast.error(error.message || 'Failed to process scan');
    }
  };

  const handleConfirmation = async (confirmed: boolean) => {
    if (confirmed) {
      try {
        const response = await apiService.handleConfirmation({
          ...state.confirmationData,
          cartonId: currentCarton.id,
          projectId: selectedProject.id
        });

        if (response.success) {
          handleSuccessfulScan(response.data);
        } else {
          toast.error(response.error.message);
        }
      } catch (error) {
        console.error('Error handling confirmation:', error);
        toast.error('Failed to process confirmation');
      }
    }
    setShowConfirmation(false);
    updateState({ confirmationData: null });
  };

  const handleConfirmSwitch = useCallback(async () => {
    if (pendingCarton) {
      console.log('[DEBUG] Switching to carton:', pendingCarton.code);
      
      // Load the new carton's contents
      const loaded = await loadCartonContents(pendingCarton.code);
      if (!loaded) {
        console.error('[ERROR] Failed to load new carton contents');
        toast.error('Failed to load carton contents');
        return;
      }
      
      // Switch to content scanning mode
      updateState({ scanningMode: 'content' });
      toast.success(`Now scanning contents for carton ${pendingCarton.code}`);
      
      // Reset the pending carton and close the confirmation
      setPendingCarton(null);
      updateState({ showSwitchConfirm: false });
    }
  }, [pendingCarton, loadCartonContents]);

  const handleCancelSwitch = useCallback(() => {
    console.log('[DEBUG] Carton switch cancelled');
    setPendingCarton(null);
    updateState({ showSwitchConfirm: false });
  }, []);

  const handleProjectItemScan = async (code: string) => {
    if (!selectedProject || !detailedProject) {
      toast.error('Please select a project first');
      return false;
    }

    // Check if it's a panel in the project
    const projectPanel = detailedProject.panels?.find((p: any) => p.code === code);
    if (projectPanel) {
      return await handleProjectPanelScan(projectPanel);
    }

    // Check if it's hardware in the project
    const projectHardware = detailedProject.hardware?.find((h: any) => h.code === code);
    if (projectHardware) {
      return await handleProjectHardwareScan(projectHardware);
    }

    // Not found in project
    return false;
  };

  const handleProjectPanelScan = async (panel: any) => {
    if (!currentCarton) {
      toast.error('Please scan a carton first');
      return false;
    }

    // Check if panel is already in the current carton
    const existingPanel = (cartonContents.panels || []).find((p: any) => p.code === panel.code);
    if (existingPanel) {
      if (existingPanel.is_received) {
        toast.success(`Panel ${panel.code} already received`);
      } else {
        await handlePanelScan(existingPanel);
      }
      return true;
    }

    // Panel is not in current carton, check if it belongs to another carton
    const otherCarton = detailedProject?.cartons?.find((c: any) => 
      c.panels?.items?.some((p: any) => p.code === panel.code)
    );

    if (otherCarton) {
      // Show confirmation to switch cartons
      setPendingCarton(otherCarton);
      updateState({
        confirmationData: {
          title: 'Panel in Different Carton',
          message: `Panel ${panel.code} belongs to carton ${otherCarton.code}. Do you want to switch to this carton?`,
          onConfirm: async () => {
            await loadCartonContents(otherCarton.code);
            updateState({ currentCarton: otherCarton });
            // Find and mark the panel as received
            const panelToUpdate = otherCarton.panels.items.find((p: any) => p.code === panel.code);
            if (panelToUpdate) {
              await handlePanelScan(panelToUpdate);
            }
          },
          onCancel: () => {
            setPendingCarton(null);
          }
        }
      });
      setShowConfirmation(true);
      return true;
    }

    // Panel is not in any carton, just show info
    toast.info(`Panel ${panel.code} is not assigned to any carton`);
    return false;
  };

  const handleProjectHardwareScan = async (hardware: any) => {
    if (!currentCarton) {
      toast.error('Please scan a carton first');
      return false;
    }

    // Check if hardware is already in the current carton
    const existingHardware = (cartonContents.hardware || []).find((h: any) => h.code === hardware.code);
    if (existingHardware) {
      if (existingHardware.is_received) {
        toast.success(`Hardware ${hardware.code} already received`);
      } else {
        await handleHardwareScan(existingHardware);
      }
      return true;
    }

    // Hardware is not in current carton, check if it belongs to another carton
    const otherCarton = detailedProject?.cartons?.find((c: any) => 
      c.hardware?.items?.some((h: any) => h.code === hardware.code)
    );

    if (otherCarton) {
      // Show confirmation to switch cartons
      setPendingCarton(otherCarton);
      updateState({
        confirmationData: {
          title: 'Hardware in Different Carton',
          message: `Hardware ${hardware.code} belongs to carton ${otherCarton.code}. Do you want to switch to this carton?`,
          onConfirm: async () => {
            await loadCartonContents(otherCarton.code);
            updateState({ currentCarton: otherCarton });
            // Find and mark the hardware as received
            const hwToUpdate = otherCarton.hardware.items.find((h: any) => h.code === hardware.code);
            if (hwToUpdate) {
              await handleHardwareScan(hwToUpdate);
            }
          },
          onCancel: () => {
            setPendingCarton(null);
          }
        }
      });
      setShowConfirmation(true);
      return true;
    }

    // Hardware is not in any carton, just show info
    toast.info(`Hardware ${hardware.code} is not assigned to any carton`);
    return false;
  };

  // Add function to handle menu item clicks
  const handleMenuItemClick = (action: string) => {
    switch (action) {
      case 'switch-project':
        updateState({ showProjectSelector: true });
        break;
      case 'scan-carton':
        updateState({ showScanner: true, scanningMode: 'carton' });
        break;
      case 'view-cartons':
        // Add your view cartons logic here
        break;
      case 'view-statistics':
        // Add your view statistics logic here
        break;
    }
    updateState({ isMobileMenuOpen: false });
  };

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isMobileMenuOpen && !target.closest('.mobile-menu') && !target.closest('.mobile-menu-button')) {
        updateState({ isMobileMenuOpen: false });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  // Carton stats type with comprehensive tracking
  type CartonStats = {
    // Panel tracking
    totalPanels: number;
    receivedPanels: number;
    pendingPanels: number;
    
    // Hardware tracking
    totalHardware: number;
    receivedHardware: number;
    pendingHardware: number;
    
    // Overall status
    totalItems: number;
    receivedItems: number;
    pendingItems: number;
    notFoundItems: number;
    
    // Progress
    progress: number;
  };

  // Calculate carton stats with comprehensive tracking
  const calculateCartonStats = useCallback((): CartonStats => {
    if (!cartonContents) {
      return {
        totalPanels: 0,
        receivedPanels: 0,
        pendingPanels: 0,
        totalHardware: 0,
        receivedHardware: 0,
        pendingHardware: 0,
        totalItems: 0,
        receivedItems: 0,
        pendingItems: 0,
        notFoundItems: 0,
        progress: 0
      };
    }

    const stats: CartonStats = {
      totalPanels: 0,
      receivedPanels: 0,
      pendingPanels: 0,
      totalHardware: 0,
      receivedHardware: 0,
      pendingHardware: 0,
      totalItems: 0,
      receivedItems: 0,
      pendingItems: 0,
      notFoundItems: 0,
      progress: 0
    };

    // Process carton contents
    (cartonContents.panels || []).forEach(item => {
      if (item.isReceived) {
        stats.receivedPanels++;
        stats.receivedItems++;
      } else {
        stats.pendingPanels++;
        stats.pendingItems++;
      }
      stats.totalPanels++;
      stats.totalItems++;
    });

    (cartonContents.hardware || []).forEach(item => {
      if (item.isReceived) {
        stats.receivedHardware++;
        stats.receivedItems++;
      } else {
        stats.pendingHardware++;
        stats.pendingItems++;
      }
      stats.totalHardware++;
      stats.totalItems++;
    });

    // Calculate overall progress
    if (stats.totalItems > 0) {
      stats.progress = Math.round((stats.receivedItems / stats.totalItems) * 100);
    }

    return stats;
  }, [cartonContents]);

  // Update stats whenever carton contents change
  useEffect(() => {
    const stats = calculateCartonStats();
    scannerRef.current.updateCartonStats(stats);
  }, [calculateCartonStats]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <Container>
        <Header>
          <HeaderLeft>
            <Title>Site Receiving</Title>
          </HeaderLeft>
          <HeaderRight>
            {/* Mobile menu button removed as per user preference */}
          </HeaderRight>
        </Header>

        {!selectedProject ? (
        <EmptyState>
          <EmptyStateIcon>
            <Package size={48} />
          </EmptyStateIcon>
            <EmptyStateText>Please select a project to begin</EmptyStateText>
            <Button onClick={() => updateState({ showProjectSelector: true })}>
              Select Project
            </Button>
        </EmptyState>
        ) : (
          <>
            <ProjectSummary onClick={() => updateState({ showProjectSelector: true })}>
              <ProjectHeader>
                <div>
                  <ProjectTitle>
                    {detailedProject?.project.display_name || selectedProject.code}
                  </ProjectTitle>
                  <ProjectSubtitle>
                    {detailedProject?.project.customer_name}
                    {detailedProject?.project.so_number && ` • SO: ${detailedProject.project.so_number}`}
                  </ProjectSubtitle>
                </div>
                <SwitchButton>
                  Switch Project
                </SwitchButton>
              </ProjectHeader>
            </ProjectSummary>

      {loading ? (
        <LoadingSpinner />
            ) : detailedProject ? (
              <>
                <ProjectStats>
                  <StatCard>
                    <StatHeader>📦 Total Cartons</StatHeader>
                    <StatValue>{detailedProject.statistics.cartons.total_cartons}</StatValue>
                  </StatCard>
                  <StatCard>
                    <StatHeader>🛠 Panels</StatHeader>
                    <StatValue>{detailedProject.statistics.panels.total_panels}</StatValue>
                    <StatBreakdown>
                      <span>R: {detailedProject.statistics.panels.received_panels}</span>
                      <span>NR: {detailedProject.statistics.panels.total_panels - detailedProject.statistics.panels.received_panels}</span>
                    </StatBreakdown>
                  </StatCard>
                  <StatCard>
                    <StatHeader>⚙️ Hardware</StatHeader>
                    <StatValue>{detailedProject.statistics.hardware.total_hardware}</StatValue>
                    <StatBreakdown>
                      <span>R: {detailedProject.statistics.hardware.received_hardware}</span>
                      <span>NR: {detailedProject.statistics.hardware.total_hardware - detailedProject.statistics.hardware.received_hardware}</span>
                    </StatBreakdown>
                  </StatCard>
                </ProjectStats>

        <CartonGrid>
                  {detailedProject.cartons.map((carton: any) => (
                    <CartonCard key={carton.id}>
              <CartonHeader>
                        <div>📦 Carton {carton.code}</div>
                        <div>
                          {carton.status.is_received ? (
                            <CheckCircle2 color="green" />
                          ) : carton.timestamps.received_at ? (
                            <Clock color="orange" />
                          ) : (
                            <AlertTriangle color="red" />
                          )}
                        </div>
              </CartonHeader>
              <CartonContent>
                        <ContentSection>
                          <div>🛠 Panels</div>
                <ContentItem>
                            <span>R: {carton.panels.received}</span>
                            <span>NR: {carton.panels.total - carton.panels.received}</span>
                            <span>T: {carton.panels.total}</span>
                </ContentItem>
                        </ContentSection>
                        <ContentSection>
                          <div>⚙️ Hardware</div>
                <ContentItem>
                            <span>R: {carton.hardware.received}</span>
                            <span>NR: {carton.hardware.total - carton.hardware.received}</span>
                            <span>T: {carton.hardware.total}</span>
                </ContentItem>
                        </ContentSection>
              </CartonContent>
            </CartonCard>
          ))}
        </CartonGrid>


        {(selectedProject || !navigator.onLine) && (
          <ScanButton 
            onClick={() => updateState({ showScanner: true })}
            aria-label="Open scanner"
          >
            <QrCode size={24} />
          </ScanButton>
        )}

        {showScanner && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
          }}>
            {/* Scanner Area */}
            <div style={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column',
              position: 'relative'
            }}>
              <Scanner
                onScan={handleScan}
                onClose={() => updateState({ showScanner: false })}
                cartonCode={currentCarton?.code}
                projectId={selectedProject?.id}
                onCartonScanned={handleCartonScan}
                onPanelScanned={handlePanelScan}
                onHardwareScanned={handleHardwareScan}
                onError={(error: Error) => {
                  console.error('Scanner error:', error);
                  toast.error(error.message || 'Failed to scan barcode');
                }}
                currentCarton={currentCarton}
                cartonContents={cartonContents}
              />
            </div>

            {/* Carton Info Overlay - Bottom Panel */}
            {currentCarton && (
              <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderTop: '1px solid #e5e7eb',
                maxHeight: '50vh',
                overflowY: 'auto',
                zIndex: 1001
              }}>
                <CartonInfoSection style={{ margin: 0, background: 'transparent', border: 'none', boxShadow: 'none' }}>
                  <CartonInfoHeader>
                    <CartonInfoTitle>
                      📦 Carton: {currentCarton.code}
                    </CartonInfoTitle>
                    <div style={{ 
                      display: 'flex', 
                      gap: '0.5rem', 
                      fontSize: '0.875rem',
                      color: '#6b7280'
                    }}>
                      <span>🛠 {cartonContents.panels?.length || 0} panels</span>
                      <span>⚙️ {cartonContents.hardware?.length || 0} hardware</span>
                    </div>
                  </CartonInfoHeader>
                  
                  {/* Panels List */}
                  {(cartonContents.panels || []).length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ 
                        fontSize: '0.875rem', 
                        fontWeight: '600', 
                        color: '#374151',
                        marginBottom: '0.5rem'
                      }}>
                        🛠 Panels
                      </div>
                      <ItemsList style={{ maxHeight: '150px' }}>
                        {(cartonContents.panels || []).map((panel: any) => (
                          <ItemRow key={panel.id}>
                            <ItemInfo>
                              <span>🛠 {panel.code}</span>
                            </ItemInfo>
                            <ItemStatus $received={panel.is_received}>
                              {panel.is_received ? 'Received' : 'Pending'}
                            </ItemStatus>
                          </ItemRow>
                        ))}
                      </ItemsList>
                    </div>
                  )}

                  {/* Hardware List */}
                  {(cartonContents.hardware || []).length > 0 && (
                    <div>
                      <div style={{ 
                        fontSize: '0.875rem', 
                        fontWeight: '600', 
                        color: '#374151',
                        marginBottom: '0.5rem'
                      }}>
                        ⚙️ Hardware
                      </div>
                      <ItemsList style={{ maxHeight: '150px' }}>
                        {(cartonContents.hardware || []).map((hw: any) => (
                          <ItemRow key={hw.id}>
                            <ItemInfo>
                              <span>⚙️ {hw.code}</span>
                            </ItemInfo>
                            <ItemStatus $received={hw.is_received}>
                              {hw.is_received ? 'Received' : 'Pending'}
                            </ItemStatus>
                          </ItemRow>
                        ))}
                      </ItemsList>
                    </div>
                  )}

                  {/* Show message if no items */}
                  {(cartonContents.panels || []).length === 0 && (cartonContents.hardware || []).length === 0 && (
                    <div style={{ 
                      textAlign: 'center', 
                      color: '#6b7280', 
                      padding: '1rem',
                      fontSize: '0.875rem'
                    }}>
                      No items found in this carton
                    </div>
                  )}
                </CartonInfoSection>
              </div>
            )}
          </div>
        )}
              </>
            ) : (
              <EmptyState>
                <EmptyStateIcon>
                  <AlertTriangle size={48} />
                </EmptyStateIcon>
                <EmptyStateText>
                  {!navigator.onLine 
                    ? 'Working in offline mode. Project data will be available when online.' 
                    : 'Failed to load project data'
                  }
                </EmptyStateText>
                <Button onClick={() => updateState({ showProjectSelector: true })}>
                  {!navigator.onLine ? 'Switch Project' : 'Try Again'}
                </Button>
              </EmptyState>
            )}
          </>
        )}

        {showProjectSelector && (
          <ProjectSelector
            onClose={() => updateState({ showProjectSelector: false })}
          />
      )}

        {showConfirmation && (
          <ConfirmationDialog
            title={confirmationData?.type === 'unassigned' ? 'Unassigned Content' : 'Confirm Action'}
            message={confirmationData?.message}
            onConfirm={() => handleConfirmation(true)}
            onCancel={() => handleConfirmation(false)}
          />
      )}

      {showSwitchConfirm && (
        <ConfirmationDialog
          title="Switch Carton"
          message={`Are you sure you want to switch to carton ${pendingCarton?.code}?`}
          onConfirm={handleConfirmSwitch}
          onCancel={handleCancelSwitch}
        />
      )}
    </Container>

      <MenuOverlay 
        $isOpen={isMobileMenuOpen}
        onClick={() => updateState({ isMobileMenuOpen: false })}
      />

      <MobileMenu 
        className="mobile-menu"
        $isOpen={isMobileMenuOpen}
      >
        <MobileMenuHeader>
          <Title>Menu</Title>
          <CloseButton 
            onClick={() => updateState({ isMobileMenuOpen: false })}
            aria-label="Close menu"
          >
            <X size={24} />
          </CloseButton>
        </MobileMenuHeader>
        
        <MobileMenuContent>
          <MobileMenuItem onClick={() => handleMenuItemClick('switch-project')}>
            <Package size={20} />
            Switch Project
          </MobileMenuItem>
          <MobileMenuItem onClick={() => handleMenuItemClick('scan-carton')}>
            <QrCode size={20} />
            Scan Carton
          </MobileMenuItem>
          <MobileMenuItem onClick={() => handleMenuItemClick('view-cartons')}>
            <Box size={20} />
            View Cartons
          </MobileMenuItem>
          <MobileMenuItem onClick={() => handleMenuItemClick('view-statistics')}>
            <BarChart className="w-6 h-6" />
            View Statistics
          </MobileMenuItem>
        </MobileMenuContent>
      </MobileMenu>
    </>
  );
};

export default CartonContentReceivingPage;
